import { Edit2, Check, X, Play, Pause, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { GoogleIcon, MetaIcon } from 'components';

const CampaignMobileCard = ({
  campaign,
  isEditing = false,
  editValues = {},
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onToggleStatus,
  onClick,
  onEditValuesChange,
  currency = 'USD',
  disabled = false,
}) => {
  const [localEditValues, setLocalEditValues] = useState(editValues);

  useEffect(() => {
    setLocalEditValues(editValues);
  }, [editValues, isEditing]);

  const isPaused = campaign.status === 'PAUSED';
  const hasNoBudgetAtCampaignLevel = campaign.platform === 'meta' && !campaign.hasBudgetAtCampaignLevel;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  const getBudgetTypeLabel = (budgetType) => {
    if (budgetType === 'daily') return 'Por día';
    if (budgetType === 'lifetime') return 'Total campaña';
    if (budgetType === 'campaign_total') return 'Total campaña';
    return 'No especificado';
  };

  const getBudgetTypeClasses = () => {
    if (campaign.budgetType === 'daily') {
      return 'bg-blue-500/20 text-blue-300 border-blue-400/30';
    }
    if (campaign.budgetType === 'lifetime' || campaign.budgetType === 'campaign_total') {
      return 'bg-purple-500/20 text-purple-300 border-purple-400/30';
    }
    return 'bg-white/10 text-white/50 border-white/20';
  };

  if (isEditing) {
    const handleEditChange = (updates) => {
      const newValues = { ...localEditValues, ...updates };
      setLocalEditValues(newValues);
      if (onEditValuesChange) {
        onEditValuesChange(newValues);
      }
    };

    return (
      <div className="backdrop-blur-md border border-white/20 rounded-xl p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Header with Esporai badge - responsive layout */}
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-2">
            {campaign.isEsporaiCampaign && (
              <div className="w-5 h-5 rounded-full bg-gradient-to-r from-fuchsia-500 to-blue-600 flex items-center justify-center flex-shrink-0 mt-1">
                <img src="/isologo.blanco.png" alt="Esporai" className="w-3 h-3" />
              </div>
            )}
            <div className="inline-flex items-center flex-shrink-0">
              {campaign.platform === 'google' ? (
                <GoogleIcon width={20} height={20} variant="color" />
              ) : (
                <MetaIcon width={20} height={20} />
              )}
            </div>
          </div>
          <input
            type="text"
            value={localEditValues.name || ''}
            onChange={(e) => handleEditChange({ name: e.target.value })}
            className="w-full bg-white/10 border border-white/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-fuchsia-400 min-h-[44px]"
            placeholder="Nombre de la campaña"
            disabled={disabled}
          />
        </div>

        {/* Budget and type */}
        {!hasNoBudgetAtCampaignLevel && (
          <div className="space-y-2">
            <label className="block text-xs text-white/60">Presupuesto</label>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-1">
                <span className="text-white/70 text-xs whitespace-nowrap">{currency}</span>
                <input
                  type="number"
                  step="0.01"
                  value={localEditValues.budget || 0}
                  onChange={(e) => handleEditChange({ budget: parseFloat(e.target.value) || 0 })}
                  className="flex-1 bg-white/10 border border-white/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-fuchsia-400 min-h-[44px]"
                  placeholder="0.00"
                  disabled={disabled}
                />
              </div>
              <div className={`px-3 py-2 rounded-lg text-xs font-medium border ${getBudgetTypeClasses()} min-h-[44px] flex items-center justify-center sm:whitespace-nowrap`}>
                {getBudgetTypeLabel(campaign.budgetType)}
              </div>
            </div>
          </div>
        )}

        {hasNoBudgetAtCampaignLevel && (
          <div className="bg-white/5 border border-white/10 rounded px-3 py-2">
            <p className="text-xs text-white/60 italic">Presupuesto a nivel ad set</p>
          </div>
        )}

        {/* Dates */}
        <div className="space-y-2">
          <label className="block text-xs text-white/60">Fechas</label>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 items-stretch sm:items-center">
            <input
              type="date"
              value={localEditValues.start_date || ''}
              onChange={(e) => handleEditChange({ start_date: e.target.value })}
              className="flex-1 bg-white/10 border border-white/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-fuchsia-400 min-h-[44px]"
              disabled={disabled}
            />
            <span className="hidden sm:inline text-white/50">→</span>
            <span className="sm:hidden text-white/50 text-center py-1">a</span>
            <input
              type="date"
              value={localEditValues.end_date || ''}
              onChange={(e) => handleEditChange({ end_date: e.target.value })}
              className="flex-1 bg-white/10 border border-white/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-fuchsia-400 min-h-[44px]"
              disabled={disabled}
            />
          </div>
        </div>

        {/* Action buttons - stack on mobile, horizontal on larger screens */}
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <button
            onClick={onSaveEdit}
            disabled={disabled}
            className={`flex-1 flex items-center justify-center gap-2 min-h-[44px] rounded-lg bg-green-500/20 border border-green-400/30 text-green-300 hover:bg-green-500/30 hover:border-green-400/50 transition-all duration-300 text-sm sm:text-base ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="Guardar cambios"
          >
            <Check className="w-4 h-4" />
            <span className="font-medium">Guardar</span>
          </button>
          <button
            onClick={onCancelEdit}
            disabled={disabled}
            className="flex-1 flex items-center justify-center gap-2 min-h-[44px] rounded-lg bg-red-500/20 border border-red-400/30 text-red-300 hover:bg-red-500/30 hover:border-red-400/50 transition-all duration-300 text-sm sm:text-base"
            title="Cancelar"
          >
            <X className="w-4 h-4" />
            <span className="font-medium">Cancelar</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="backdrop-blur-md border border-white/20 rounded-xl p-3 sm:p-4 space-y-2 sm:space-y-3 cursor-pointer hover:border-white/30 transition-all duration-200 active:scale-[0.98]"
    >
      {/* Header: Esporai badge + name + platform */}
      <div className="flex items-start gap-2">
        {campaign.isEsporaiCampaign && (
          <div className="w-5 h-5 rounded-full bg-gradient-to-r from-fuchsia-500 to-blue-600 flex items-center justify-center flex-shrink-0">
            <img src="/isologo.blanco.png" alt="Esporai" className="w-3 h-3" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-sm sm:text-base line-clamp-2">{campaign.name}</h3>
        </div>
        <div className="inline-flex items-center flex-shrink-0">
          {campaign.platform === 'google' ? (
            <GoogleIcon width={20} height={20} variant="color" />
          ) : (
            <MetaIcon width={20} height={20} />
          )}
        </div>
      </div>

      {/* Budget info - responsive text sizing */}
      {hasNoBudgetAtCampaignLevel ? (
        <p className="text-white/70 text-xs italic">Presupuesto a nivel ad set</p>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
          <span className="font-semibold text-white">
            {currency} {campaign.budget?.toFixed(2) || '0.00'}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium border w-fit ${getBudgetTypeClasses()}`}>
            {getBudgetTypeLabel(campaign.budgetType)}
          </span>
        </div>
      )}

      {/* Dates - responsive text sizing */}
      <div className="text-xs sm:text-sm text-white/70 truncate">
        {formatDate(campaign.start_date)} → {formatDate(campaign.end_date)}
      </div>

      {/* Divider */}
      <div className="border-t border-white/10" />

      {/* Action buttons - responsive layout */}
      <div
        className="flex items-center gap-1 sm:gap-2 justify-end flex-wrap"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Play/Pause button */}
        <button
          onClick={onToggleStatus}
          disabled={disabled}
          className={`flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg border transition-all duration-300 flex-shrink-0 ${
            isPaused
              ? 'bg-green-500/20 border-green-400/30 text-green-300 hover:bg-green-500/30 hover:border-green-400/50'
              : 'bg-yellow-500/20 border-yellow-400/30 text-yellow-300 hover:bg-yellow-500/30 hover:border-yellow-400/50'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={isPaused ? 'Activar campaña' : 'Pausar campaña'}
        >
          {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
        </button>

        {/* Edit button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onStartEdit();
          }}
          disabled={disabled}
          className={`flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg bg-blue-500/20 border border-blue-400/30 text-blue-300 hover:bg-blue-500/30 hover:border-blue-400/50 transition-all duration-300 flex-shrink-0 ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          title="Editar"
        >
          <Edit2 className="w-4 h-4" />
        </button>

        {/* Navigate button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg bg-fuchsia-500/20 border border-fuchsia-400/30 text-fuchsia-300 hover:bg-fuchsia-500/30 hover:border-fuchsia-400/50 transition-all duration-300 flex-shrink-0"
          title="Ver detalles"
        >
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CampaignMobileCard;
